import { PerioSiteFive } from '@utils/perio-line.utils'
import { getToothType } from '../tooth-svgs/config/tooth-config'

/** SVG viewBox matches buccal/palatal/lingual tooth assets. */
export const PERIO_VIEW_W = 80
export const PERIO_VIEW_H = 200
/**
 * Fallback inset when tooth type is unknown.
 * Larger inset = M/D closer to center; smaller = closer to edges.
 */
export const PERIO_X_INSET = 14
/** M/D inset by tooth morphology (viewBox units, width 80). */
const PERIO_X_INSET_BY_TYPE: Record<
  'molar' | 'premolar' | 'canine' | 'incisor',
  number
> = {
  molar: 10,
  premolar: 14,
  canine: 16,
  incisor: 18,
}

/**
 * Near-top CEJ baseline (used for lower buccal 17–32).
 * Positive PD grows toward apex (down in the viewBox).
 */
export const PERIO_CEJ_Y_TOP = 65
/**
 * Near-bottom CEJ baseline (used for upper buccal 1–16).
 * Positive PD grows toward apex (up in the viewBox).
 */
export const PERIO_CEJ_Y_BOTTOM = PERIO_VIEW_H - PERIO_CEJ_Y_TOP
/** ViewBox units per mm. */
export const PERIO_MM_SCALE = 6
export const PERIO_MM_MIN = -5
export const PERIO_MM_MAX = 15

export const PERIO_REF_STROKE = '#aaaeb6'
export const PERIO_SERIES_A_STROKE = '#24a1a3'
export const PERIO_SERIES_B_STROKE = '#14341f'

export const getPerioXInset = (toothId: number): number => {
  const type = getToothType(toothId)
  return PERIO_X_INSET_BY_TYPE[type] ?? PERIO_X_INSET
}

/**
 * Five x positions: edges (P1/P5 always 0 mm) + three clinical sites.
 * P2/P4 (M or D) inset varies by tooth type so sites sit on the crown body.
 */
export const perioFiveSiteXs = (
  toothId: number,
): [number, number, number, number, number] => {
  const inset = getPerioXInset(toothId)
  return [0, inset, PERIO_VIEW_W / 2, PERIO_VIEW_W - inset, PERIO_VIEW_W]
}

/** True for teeth 1–16 (upper jaw). */
export const isUpperTooth = (toothId: number): boolean =>
  toothId >= 1 && toothId <= 16

/**
 * Map mm from CEJ to viewBox Y.
 * Upper buccal (1–16): baseline near bottom, PD grows up.
 * Lower buccal (17–32): baseline near top, PD grows down.
 */
export const perioMmToY = (mm: number, isUpper: boolean): number => {
  const clamped = Math.max(PERIO_MM_MIN, Math.min(PERIO_MM_MAX, mm))
  if (isUpper) {
    return PERIO_CEJ_Y_BOTTOM - clamped * PERIO_MM_SCALE
  }
  return PERIO_CEJ_Y_TOP + clamped * PERIO_MM_SCALE
}

interface PerioPoint {
  x: number
  y: number
}

/** Catmull–Rom spline through points, converted to cubic Bezier SVG path. */
const pointsToSmoothPath = (points: PerioPoint[]): string => {
  if (points.length === 0) {
    return ''
  }
  if (points.length === 1) {
    return `M ${points[0].x} ${points[0].y}`
  }
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`
  }

  let path = `M ${points[0].x} ${points[0].y}`
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] ?? points[i + 1]
    const cp1x = p1.x + (p2.x - p0.x) / 6
    const cp1y = p1.y + (p2.y - p0.y) / 6
    const cp2x = p2.x - (p3.x - p1.x) / 6
    const cp2y = p2.y - (p3.y - p1.y) / 6
    path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2.x} ${p2.y}`
  }
  return path
}

export const perioFivePointsToPath = (
  sites: PerioSiteFive,
  isUpper: boolean,
  toothId: number,
): string => {
  const xs = perioFiveSiteXs(toothId)
  const points: PerioPoint[] = sites.map((mm, i) => ({
    x: xs[i],
    y: perioMmToY(mm, isUpper),
  }))
  return pointsToSmoothPath(points)
}

/** Fraction of tooth SVG width for synthetic edge points (P1 / P5). */
export const PERIO_LEFT_EDGE_FRAC = 0
export const PERIO_RIGHT_EDGE_FRAC = 1
